import { useEffect, useState, useRef, useCallback, forwardRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  Card,
  Button,
  CircularProgress,
  Alert,
  Container,
  Chip,
  Avatar,
  Skeleton,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import mapboxgl from "mapbox-gl";
import { tokens } from "../../theme";
import { useAuth } from "../../hooks/useAuth";
import {
  FiUsers,
  FiTruck,
  FiMapPin,
  FiNavigation,
  FiPhone,
  FiTrash2,
  FiRefreshCw,
  FiMaximize2,
  FiActivity,
} from "react-icons/fi";
import { format } from "date-fns";
import Header from "../../components/Header";
import AttendanceChart from "../../components/AttendanceChart";
import io from "socket.io-client";
import { MAIN_URL } from "../../data/const";
import "./index.css";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Custom StatBox Component with glassmorphism
const GlassStatBox = ({ title, subtitle, icon, trend, trendUp, colors }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: "16px",
        background: `linear-gradient(135deg, ${colors.greenAccent[500]}20, ${colors.greenAccent[600]}30)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: colors.greenAccent[400],
        fontSize: 24,
      }}
    >
      {icon}
    </Box>
    <Box flex={1}>
      <Typography
        variant="h3"
        fontWeight="700"
        color={colors.grey[100]}
        sx={{ lineHeight: 1.2 }}
      >
        {title}
      </Typography>
      <Typography variant="body2" color={colors.grey[400]} mt={0.5}>
        {subtitle}
      </Typography>
    </Box>
    {trend && (
      <Chip
        label={trend}
        size="small"
        sx={{
          backgroundColor: trendUp
            ? `${colors.greenAccent[500]}20`
            : `${colors.redAccent[500]}20`,
          color: trendUp ? colors.greenAccent[400] : colors.redAccent[400],
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    )}
  </Box>
);

// Vehicle Card Component
const VehicleCard = ({ vehicle, onClick, isSelected, colors }) => (
  <Card
    onClick={onClick}
    sx={{
      p: 2.5,
      background: isSelected
        ? `linear-gradient(135deg, ${colors.greenAccent[700]}30, ${colors.greenAccent[600]}20)`
        : `linear-gradient(135deg, ${colors.primary[500]}90, ${colors.primary[600]}80)`,
      backdropFilter: "blur(10px)",
      border: `1px solid ${
        isSelected ? colors.greenAccent[500] : colors.primary[300]
      }40`,
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: "16px",
      "&:hover": {
        transform: "translateY(-4px) scale(1.02)",
        boxShadow: `0 20px 40px ${colors.primary[900]}60`,
        border: `1px solid ${colors.greenAccent[500]}60`,
      },
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.greenAccent[600]})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        <FiTruck size={20} />
      </Box>
      <Box>
        <Typography
          color={colors.grey[100]}
          variant="subtitle1"
          fontWeight="700"
        >
          {vehicle.vehicleNum || "Vehicle"}
        </Typography>
        <Chip
          label="Active"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            backgroundColor: `${colors.greenAccent[500]}20`,
            color: colors.greenAccent[400],
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>
    </Box>

    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <FiMapPin size={14} color={colors.greenAccent[400]} />
        <Typography variant="body2" color={colors.grey[300]} noWrap>
          {vehicle.sourceLocation}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <FiNavigation size={14} color={colors.blueAccent[400]} />
        <Typography variant="body2" color={colors.grey[300]} noWrap>
          {vehicle.destinationLocation}
        </Typography>
      </Box>
    </Box>
  </Card>
);

// Driver Card Component
const DriverCard = ({ driver, colors, theme }) => (
  <Card
    sx={{
      background: `linear-gradient(135deg, ${colors.primary[500]}90, ${colors.primary[600]}80)`,
      backdropFilter: "blur(10px)",
      border: `1px solid ${colors.primary[300]}30`,
      p: 2.5,
      borderRadius: "16px",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: `0 20px 40px ${colors.primary[900]}60`,
        border: `1px solid ${colors.greenAccent[500]}40`,
      },
    }}
  >
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Avatar
        sx={{
          width: 48,
          height: 48,
          background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.greenAccent[700]})`,
          fontSize: "1.1rem",
          fontWeight: 700,
        }}
      >
        {driver.username?.charAt(0)?.toUpperCase()}
      </Avatar>
      <Box>
        <Typography
          variant="subtitle1"
          color={colors.grey[100]}
          fontWeight="700"
        >
          {driver.username}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          ID: {driver._id?.slice(-6)}
        </Typography>
      </Box>
    </Box>

    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <FiPhone size={14} color={colors.grey[400]} />
      <Typography variant="body2" color={colors.grey[300]}>
        {driver.phone}
      </Typography>
    </Box>

    <Button
      variant="outlined"
      size="small"
      startIcon={<FiTrash2 size={14} />}
      sx={{
        color: colors.redAccent[400],
        borderColor: `${colors.redAccent[400]}40`,
        borderRadius: "10px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
          borderColor: colors.redAccent[400],
          backgroundColor: `${colors.redAccent[400]}10`,
        },
      }}
    >
      Remove
    </Button>
  </Card>
);

// Glass Card Component with forwardRef to work with Fade
const GlassCard = forwardRef(({ children, sx = {}, colors, ...props }, ref) => (
  <Card
    ref={ref}
    {...props}
    sx={{
      background: `linear-gradient(135deg, ${colors.primary[400]}95, ${colors.primary[500]}90)`,
      backdropFilter: "blur(20px)",
      border: `1px solid ${colors.primary[300]}30`,
      borderRadius: "20px",
      boxShadow: `0 8px 32px ${colors.primary[900]}40`,
      ...sx,
    }}
  >
    {children}
  </Card>
));

GlassCard.displayName = "GlassCard";

const Dashboard = () => {
  const { user, api } = useAuth();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const socket = useRef(null);
  const markersRef = useRef({});
  const [lng] = useState(77.1666);
  const [lat] = useState(31.9165);
  const [zoom] = useState(9);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [socketVehicles, setSocketVehicles] = useState({});
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [ownerData, setOwnerData] = useState({
    id: "",
    name: "",
    drivers: [],
    vehicles: [],
    business: "",
    image: "",
    phone: "",
    email: "",
  });

  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceError, setAttendanceError] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  // Create custom marker element
  const createCustomMarker = useCallback(
    (vehicle, isSelected) => {
      const el = document.createElement("div");
      el.className = `custom-marker ${isSelected ? "selected" : ""}`;
      el.innerHTML = `
      <div class="marker-container">
        <div class="marker-pulse"></div>
        <div class="marker-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 16V8C4 6.89543 4.89543 6 6 6H18C19.1046 6 20 6.89543 20 8V16C20 17.1046 19.1046 18 18 18H6C4.89543 18 4 17.1046 4 16Z" fill="${
              colors.greenAccent[500]
            }"/>
            <rect x="6" y="8" width="4" height="3" rx="0.5" fill="white" opacity="0.8"/>
            <rect x="14" y="8" width="4" height="3" rx="0.5" fill="white" opacity="0.8"/>
            <circle cx="7" cy="18" r="2" fill="${colors.grey[800]}" stroke="${
        colors.grey[600]
      }" stroke-width="1"/>
            <circle cx="17" cy="18" r="2" fill="${colors.grey[800]}" stroke="${
        colors.grey[600]
      }" stroke-width="1"/>
          </svg>
        </div>
        <div class="marker-label">${vehicle.vehicleNum || "Bus"}</div>
      </div>
    `;
      return el;
    },
    [colors]
  );

  // Fetch owner data
  useEffect(() => {
    const fetchOwnerData = async () => {
      setDataLoading(true);
      try {
        const response = await api.get(`/owner/${user._id}`);
        const { data } = response.data;

        setOwnerData({
          id: data._id,
          name: data.username,
          drivers: data.Driver || [],
          vehicles: data.Vehicle || [],
          business: data.business,
          image: data.images[0]?.url,
          phone: data.phone,
          email: data.email,
        });
      } catch (error) {
        console.error("Error fetching owner data:", error);
      } finally {
        setDataLoading(false);
      }
    };

    if (user?._id) {
      fetchOwnerData();
    }
  }, [user, api]);

  // Fetch attendance data
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setAttendanceLoading(true);
        const response = await api.get(`/attendance/${user._id}`);

        if (!response.data?.data?.attendance) {
          throw new Error("Invalid attendance data structure");
        }

        const parseTimeString = (timeStr) => {
          if (!timeStr) return null;
          try {
            if (timeStr.includes("T")) {
              return new Date(timeStr);
            }
            const today = new Date();
            const [hours, minutes, seconds] = timeStr.split(":").map(Number);
            return new Date(today.setHours(hours, minutes, seconds || 0));
          } catch (error) {
            return null;
          }
        };

        const calculateHours = (startTime, endTime) => {
          const start = parseTimeString(startTime);
          const end = parseTimeString(endTime);
          if (!start || !end) return 0;
          let diffHours = (end - start) / (1000 * 60 * 60);
          if (diffHours < 0) diffHours += 24;
          return diffHours;
        };

        const processedData = response.data.data.attendance.reduce(
          (acc, day) => {
            const date = format(new Date(day.date), "MM/dd");
            const dailyHours = day.trips.reduce((tripAcc, trip) => {
              return tripAcc + calculateHours(trip.start_time, trip.end_time);
            }, 0);

            if (!acc[date]) {
              acc[date] = { totalHours: 0, tripCount: 0, trips: [] };
            }
            acc[date].totalHours += dailyHours;
            acc[date].tripCount += day.trips.length;
            return acc;
          },
          {}
        );

        const chartData = [
          {
            id: "Working Hours",
            data: Object.entries(processedData).map(([date, data]) => ({
              x: date,
              y: data.totalHours.toFixed(2),
              tripCount: data.tripCount,
            })),
          },
        ];

        if (response.data.data.stats) {
          chartData.push({
            id: "Average Per Driver",
            data: Object.entries(response.data.data.stats).map(
              ([_, stats]) => ({
                x: stats.driverName,
                y: Math.abs(stats.averageHoursPerDay).toFixed(2),
              })
            ),
          });
        }

        setAttendanceData(chartData);
        setAttendanceError(null);
      } catch (error) {
        console.error("Error fetching attendance:", error);
        setAttendanceError("Failed to load attendance data");
      } finally {
        setAttendanceLoading(false);
      }
    };

    if (user?._id) {
      fetchAttendance();
    }
  }, [user, api]);

  // Socket Connection
  useEffect(() => {
    socket.current = io(MAIN_URL);
    socket.current.emit("globalTripsData");

    socket.current.on("updateGlobalTripsData", async (allTrips) => {
      const ownerTrips = {};
      const tripIds = Object.keys(allTrips || {});
      const STALE_MS = 60_000;
      const now = Date.now();

      for (const tripId of tripIds) {
        const tripData = allTrips[tripId];
        if (!tripData) continue;

        // Drop stale entries — Flutter clients may have gone silent without
        // emitting tripEnded.
        if (tripData.currentTime) {
          const ts = Date.parse(tripData.currentTime);
          if (!Number.isNaN(ts) && now - ts >= STALE_MS) continue;
        }

        let vehicleNum = tripData.vehicleNum;
        if (!vehicleNum) {
          try {
            const response = await fetch(
              `${MAIN_URL}/himraahi/trip/Coords/${tripId}`
            );
            if (response.ok) {
              const tripDetails = await response.json();
              vehicleNum = tripDetails.Vehicle;
            }
          } catch (err) {
            console.error("Error fetching trip details:", err);
          }
        }

        const belongsToOwner = ownerData.vehicles.some(
          (vehicle) => vehicle.vehicleNum === vehicleNum
        );
        if (belongsToOwner) {
          ownerTrips[tripId] = { ...tripData, tripID: tripId, vehicleNum };
        }
      }

      setSocketVehicles(ownerTrips);
    });

    socket.current.on("broadcastDriverData", (data) => {
      const belongsToOwner = ownerData.vehicles.some(
        (vehicle) => vehicle.vehicleNum === data.vehicleNum
      );

      if (belongsToOwner) {
        setSocketVehicles((prev) => ({
          ...prev,
          [data.tripId || data.tripID]: data,
        }));
      }
    });

    socket.current.on("tripEnded", ({ tripId }) => {
      setSocketVehicles((prev) => {
        const updated = { ...prev };
        delete updated[tripId];
        return updated;
      });
    });

    return () => socket.current?.disconnect();
  }, [ownerData.vehicles]);

  // Fetch Route Geometry
  const fetchRouteGeometry = async (vehicle) => {
    try {
      const response = await fetch(
        `${MAIN_URL}/api/v1/himraahi/trip/Coords/${vehicle.tripID}`
      );
      const tripData = await response.json();

      const { coordinateStart, coordinateEnd, via } = tripData.data;
      console.log("coord", tripData);
      // via is an empty string "", not an array - check if it's a valid array with data
      const hasVia = Array.isArray(via) && via.length > 0;
      const waypoints = hasVia
        ? `${coordinateStart[1]},${coordinateStart[0]};${via[1]},${via[0]};${coordinateEnd[1]},${coordinateEnd[0]}`
        : `${coordinateStart[1]},${coordinateStart[0]};${coordinateEnd[1]},${coordinateEnd[0]}`;

      const routeResponse = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${waypoints}?alternatives=true&geometries=geojson&access_token=${mapboxgl.accessToken}`
      );
      const routeData = await routeResponse.json();
      setRouteGeometry(routeData.routes[0]?.geometry);
    } catch (error) {
      console.error("Error fetching route:", error);
    }
  };

  // Initialize Map with enhanced styling
  useEffect(() => {
    if (!map.current && mapContainer.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/navigation-night-v1",
        center: [lng, lat],
        zoom: zoom,
        pitch: 45,
        bearing: -10,
        antialias: true,
      });

      map.current.on("load", () => {
        setMapLoaded(true);

        // Add 3D buildings layer
        const layers = map.current.getStyle().layers;
        const labelLayerId = layers.find(
          (layer) => layer.type === "symbol" && layer.layout["text-field"]
        )?.id;

        map.current.addLayer(
          {
            id: "3d-buildings",
            source: "composite",
            "source-layer": "building",
            filter: ["==", "extrude", "true"],
            type: "fill-extrusion",
            minzoom: 12,
            paint: {
              "fill-extrusion-color": "#1a1a2e",
              "fill-extrusion-height": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12,
                0,
                12.5,
                ["get", "height"],
              ],
              "fill-extrusion-base": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12,
                0,
                12.5,
                ["get", "min_height"],
              ],
              "fill-extrusion-opacity": 0.7,
            },
          },
          labelLayerId
        );

        // Add fog for atmosphere
        if (map.current.setFog) {
          map.current.setFog({
            color: "rgb(20, 20, 30)",
            "high-color": "rgb(40, 40, 60)",
            "horizon-blend": 0.1,
            "space-color": "rgb(10, 10, 20)",
            "star-intensity": 0.3,
          });
        }
      });

      map.current.addControl(
        new mapboxgl.NavigationControl({ visualizePitch: true }),
        "top-right"
      );

      map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");
    }
  }, [lng, lat, zoom]);

  // Update Map Markers with custom styling
  useEffect(() => {
    if (map.current && mapLoaded && socketVehicles) {
      // Remove existing markers
      Object.values(markersRef.current).forEach((marker) => marker.remove());
      markersRef.current = {};

      Object.values(socketVehicles).forEach((vehicle) => {
        const el = createCustomMarker(
          vehicle,
          selectedVehicle?.tripID === vehicle.tripID
        );

        // Defensive coercion: pre-fix Redis entries (or any future malformed
        // payload) may carry objects here — interpolating an object into a
        // template literal would crash popup creation and white-screen the page.
        const asText = (v) =>
          typeof v === "string" ? v : v == null ? "" : String(v);
        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: false,
          className: "custom-popup",
        }).setHTML(`
          <div class="popup-content">
            <div class="popup-header">
              <span class="popup-title">${
                asText(vehicle.vehicleNum) || "Vehicle"
              }</span>
              <span class="popup-badge">Live</span>
            </div>
            <div class="popup-body">
              <div class="popup-row">
                <span class="popup-label">From</span>
                <span class="popup-value">${asText(vehicle.sourceLocation)}</span>
              </div>
              <div class="popup-row">
                <span class="popup-label">To</span>
                <span class="popup-value">${asText(vehicle.destinationLocation)}</span>
              </div>
            </div>
          </div>
        `);

        const marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([vehicle.longitude, vehicle.latitude])
          .setPopup(popup)
          .addTo(map.current);

        markersRef.current[vehicle.tripID] = marker;

        el.addEventListener("click", () => {
          setSelectedVehicle(vehicle);
          fetchRouteGeometry(vehicle);
          map.current.flyTo({
            center: [vehicle.longitude, vehicle.latitude],
            zoom: 14,
            pitch: 60,
            bearing: 30,
            duration: 2000,
            essential: true,
          });
        });
      });
    }
  }, [socketVehicles, mapLoaded, selectedVehicle, createCustomMarker]);

  // Draw Route with gradient
  useEffect(() => {
    if (map.current && mapLoaded && routeGeometry) {
      if (map.current.getLayer("route")) {
        map.current.removeLayer("route");
      }
      if (map.current.getLayer("route-glow")) {
        map.current.removeLayer("route-glow");
      }
      if (map.current.getSource("route")) {
        map.current.removeSource("route");
      }

      map.current.addSource("route", {
        type: "geojson",
        data: { type: "Feature", properties: {}, geometry: routeGeometry },
      });

      // Glow effect
      map.current.addLayer({
        id: "route-glow",
        type: "line",
        source: "route",
        paint: {
          "line-color": colors.greenAccent[500],
          "line-width": 12,
          "line-blur": 8,
          "line-opacity": 0.4,
        },
      });

      // Main route line
      map.current.addLayer({
        id: "route",
        type: "line",
        source: "route",
        paint: {
          "line-color": colors.greenAccent[400],
          "line-width": 4,
          "line-opacity": 0.9,
        },
      });

      const coordinates = routeGeometry.coordinates;
      const bounds = coordinates.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
      );

      map.current.fitBounds(bounds, { padding: 80, pitch: 45, duration: 1500 });
    }
  }, [routeGeometry, mapLoaded, colors]);

  // Add this new useEffect after the "Update Map Markers" useEffect
  useEffect(() => {
    if (map.current && mapLoaded && Object.keys(socketVehicles).length > 0) {
      const vehicles = Object.values(socketVehicles);

      // If only one vehicle, center on it
      if (vehicles.length === 1) {
        map.current.flyTo({
          center: [vehicles[0].longitude, vehicles[0].latitude],
          zoom: 12,
          pitch: 45,
          duration: 2000,
        });
      }
      // If multiple vehicles, fit bounds to show all
      else if (vehicles.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        vehicles.forEach((vehicle) => {
          bounds.extend([vehicle.longitude, vehicle.latitude]);
        });

        map.current.fitBounds(bounds, {
          padding: { top: 100, bottom: 100, left: 100, right: 100 },
          maxZoom: 14,
          duration: 2000,
        });
      }
    }
  }, [socketVehicles, mapLoaded]);

  const activeVehicleCount = Object.keys(socketVehicles).length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%)`,
        pb: 4,
      }}
    >
      <Container maxWidth="xl" sx={{ pt: 3 }}>
        {/* Header */}
        <Fade in timeout={500}>
          <Box mb={4}>
            <Header
              title={
                dataLoading ? (
                  <Skeleton width={250} />
                ) : (
                  `${ownerData.name}'s Dashboard`
                )
              }
              subtitle="Fleet management and live tracking"
            />
          </Box>
        </Fade>

        {/* Stats Section */}
        <Box
          display="grid"
          gap={3}
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          mb={4}
        >
          <Fade in timeout={600}>
            <div>
              <GlassCard colors={colors}>
                <GlassStatBox
                  title={
                    dataLoading ? (
                      <Skeleton width={40} />
                    ) : (
                      ownerData.drivers.length.toString()
                    )
                  }
                  subtitle="Active Drivers"
                  icon={<FiUsers />}
                  colors={colors}
                />
              </GlassCard>
            </div>
          </Fade>

          <Fade in timeout={700}>
            <div>
              <GlassCard colors={colors}>
                <GlassStatBox
                  title={
                    dataLoading ? (
                      <Skeleton width={40} />
                    ) : (
                      ownerData.vehicles.length.toString()
                    )
                  }
                  subtitle="Total Vehicles"
                  icon={<FiTruck />}
                  colors={colors}
                />
              </GlassCard>
            </div>
          </Fade>

          <Fade in timeout={800}>
            <div>
              <GlassCard colors={colors}>
                <GlassStatBox
                  title={activeVehicleCount.toString()}
                  subtitle="Live Vehicles"
                  icon={<FiActivity />}
                  trend={activeVehicleCount > 0 ? "Online" : "Offline"}
                  trendUp={activeVehicleCount > 0}
                  colors={colors}
                />
              </GlassCard>
            </div>
          </Fade>

          <Fade in timeout={900}>
            <div>
              <GlassCard colors={colors}>
                <GlassStatBox
                  title={ownerData.business || "-"}
                  subtitle="Business"
                  icon={<FiMapPin />}
                  colors={colors}
                />
              </GlassCard>
            </div>
          </Fade>
        </Box>

        {/* Map Section */}
        <Fade in timeout={1000}>
          <div>
            <GlassCard colors={colors} sx={{ overflow: "hidden", mb: 4 }}>
              <Box
                p={2.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom={`1px solid ${colors.primary[300]}30`}
              >
                <Box>
                  <Typography
                    variant="h5"
                    color={colors.grey[100]}
                    fontWeight="700"
                  >
                    Live Fleet Tracking
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor:
                          activeVehicleCount > 0
                            ? colors.greenAccent[500]
                            : colors.grey[500],
                        animation:
                          activeVehicleCount > 0 ? "pulse 2s infinite" : "none",
                      }}
                    />
                    <Typography variant="body2" color={colors.grey[400]}>
                      {activeVehicleCount} vehicle
                      {activeVehicleCount !== 1 ? "s" : ""} active
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      sx={{
                        color: colors.grey[300],
                        "&:hover": { color: colors.greenAccent[400] },
                      }}
                      onClick={() => socket.current?.emit("globalTripsData")}
                    >
                      <FiRefreshCw size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Fullscreen">
                    <IconButton
                      size="small"
                      sx={{
                        color: colors.grey[300],
                        "&:hover": { color: colors.greenAccent[400] },
                      }}
                    >
                      <FiMaximize2 size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box
                ref={mapContainer}
                sx={{
                  width: "100%",
                  height: { xs: 400, md: 500 },
                  position: "relative",
                }}
              />
            </GlassCard>
          </div>
        </Fade>

        {/* Active Vehicles Grid */}
        {activeVehicleCount > 0 && (
          <Fade in timeout={1100}>
            <div>
              <GlassCard colors={colors} sx={{ p: 3, mb: 4 }}>
                <Typography
                  variant="h5"
                  color={colors.grey[100]}
                  fontWeight="700"
                  mb={3}
                >
                  Active Vehicles
                </Typography>
                <Box
                  display="grid"
                  gap={2}
                  gridTemplateColumns={{
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                >
                  {Object.values(socketVehicles).map((vehicle) => (
                    <VehicleCard
                      key={vehicle.tripID}
                      vehicle={vehicle}
                      isSelected={selectedVehicle?.tripID === vehicle.tripID}
                      colors={colors}
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        fetchRouteGeometry(vehicle);
                        map.current?.flyTo({
                          center: [vehicle.longitude, vehicle.latitude],
                          zoom: 14,
                          pitch: 60,
                          duration: 1500,
                        });
                      }}
                    />
                  ))}
                </Box>
              </GlassCard>
            </div>
          </Fade>
        )}

        {/* Attendance Chart */}
        <Fade in timeout={1200}>
          <div>
            <GlassCard colors={colors} sx={{ p: 3, mb: 4, minHeight: 400 }}>
              <Typography
                variant="h5"
                color={colors.grey[100]}
                fontWeight="700"
                mb={3}
              >
                Driver Attendance Overview
              </Typography>

              {attendanceLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="300px"
                >
                  <CircularProgress sx={{ color: colors.greenAccent[500] }} />
                </Box>
              ) : attendanceError ? (
                <Alert
                  severity="error"
                  sx={{
                    backgroundColor: `${colors.redAccent[500]}20`,
                    color: colors.redAccent[400],
                    border: `1px solid ${colors.redAccent[500]}40`,
                  }}
                >
                  {attendanceError}
                </Alert>
              ) : (
                <Box height={350}>
                  <AttendanceChart data={attendanceData} />
                </Box>
              )}
            </GlassCard>
          </div>
        </Fade>

        {/* Drivers Section */}
        <Fade in timeout={1300}>
          <div>
            <GlassCard colors={colors}>
              <Box
                p={2.5}
                borderBottom={`1px solid ${colors.primary[300]}30`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    variant="h5"
                    color={colors.grey[100]}
                    fontWeight="700"
                  >
                    Registered Drivers
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]} mt={0.5}>
                    {ownerData.drivers.length} driver
                    {ownerData.drivers.length !== 1 ? "s" : ""} in your fleet
                  </Typography>
                </Box>
              </Box>

              <Box
                p={2.5}
                display="grid"
                gap={2}
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
              >
                {dataLoading
                  ? [...Array(3)].map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        height={150}
                        sx={{
                          borderRadius: "16px",
                          bgcolor: colors.primary[500],
                        }}
                      />
                    ))
                  : ownerData.drivers.map((driver) => (
                      <DriverCard
                        key={driver._id}
                        driver={driver}
                        colors={colors}
                        theme={theme}
                      />
                    ))}
              </Box>

              {!dataLoading && ownerData.drivers.length === 0 && (
                <Box
                  p={4}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiUsers size={48} color={colors.grey[500]} />
                  <Typography color={colors.grey[400]} mt={2}>
                    No drivers registered yet
                  </Typography>
                </Box>
              )}
            </GlassCard>
          </div>
        </Fade>
      </Container>
    </Box>
  );
};

export default Dashboard;
